import { gql, type TypedDocumentNode } from "@apollo/client";
import type { EventDefaultsQueryData, EventDefaultsQueryVariables } from "./_form/types";

export const EVENTS_QUERY = gql`
  query GetBackofficeEvents(
    $query: BackofficeEventsSearchInput
    $orderBy: BackofficeEventsEventOrderByInput
    $first: Int
    $last: Int
    $after: String
    $before: String
    $eventTypes: [String!]
  ) {
    events(
      query: $query
      orderBy: $orderBy
      first: $first
      last: $last
      after: $after
      before: $before
      eventTypes: $eventTypes
    ) {
      edges {
        node {
          id
          slug
          status
          cancelledAt
          endsAt
          startsAt
          updatedAt
          sessionCount
          registrationCount
          registrationType
          title
          eventCode
          description
          eventType {
            id
            name
          }
          activityType {
            id
            title
          }
          section {
            id
            name
            legacyId
            permissions {
              manageEvents
            }
          }
          facility {
            id
            name
          }
          locationType
          location
          address1
          city
          state
          zip
          timezone
          bannerImageUrl
          sessions {
            id
            startsAt
            endsAt
            maxAttendance
          }
          registrationOpenAt
          registrationCloseAt
          hasWaitlist
          customFields
          termsContent
          hasActiveOrders
          patEventAttributes {
            courseRatingFemale
            courseRatingMale
            examiner {
              id
            }
            hostProfessional {
              id
            }
            yardageFemale
            yardageFemaleFiftyPlus
            yardageMale
            yardageMaleFiftyPlus
            collectionType
            registrationFeeCents
            courseFeeCents
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

export const UNIVERSITY_EVENTS_QUERY = gql`
  query GetUniversityBackofficeEvents(
    $query: BackofficeEventsSearchInput
    $orderBy: BackofficeEventsEventOrderByInput
    $first: Int
    $last: Int
    $after: String
    $before: String
    $eventTypes: [String!]
  ) {
    events(
      query: $query
      orderBy: $orderBy
      first: $first
      last: $last
      after: $after
      before: $before
      eventTypes: $eventTypes
    ) {
      edges {
        node {
          id
          slug
          status
          cancelledAt
          endsAt
          startsAt
          updatedAt
          sessionCount
          registrationCount
          registrationType
          title
          eventCode
          description
          activityTypeSummary {
            id
            title
          }
          eventTypeSummary {
            id
            name
          }
          section {
            id
            name
            legacyId
            permissions {
              manageEvents
            }
          }
          facility {
            id
            name
          }
          locationType
          location
          address1
          city
          state
          zip
          timezone
          bannerImageUrl
          sessions {
            id
            startsAt
            endsAt
            maxAttendance
          }
          registrationOpenAt
          registrationCloseAt
          hasWaitlist
          customFields
          termsContent
          hasActiveOrders
          patEventAttributes {
            courseRatingFemale
            courseRatingMale
            examiner {
              id
            }
            hostProfessional {
              id
            }
            yardageFemale
            yardageFemaleFiftyPlus
            yardageMale
            yardageMaleFiftyPlus
            collectionType
            registrationFeeCents
            courseFeeCents
          }
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

export type EventsSearchInputFieldsData = {
  __type: {
    inputFields: { name: string }[];
  } | null;
};

export const EVENTS_SEARCH_INPUT_FIELDS_QUERY: TypedDocumentNode<EventsSearchInputFieldsData> = gql`
  query EventsSearchInputFields {
    __type(name: "BackofficeEventsSearchInput") {
      inputFields {
        name
      }
    }
  }
`;

export function isEventCodeSearchSupported(data: EventsSearchInputFieldsData | undefined): boolean {
  return data?.__type?.inputFields?.some((field) => field.name === "eventCode") ?? false;
}

export const GET_EVENT_TYPES = gql`
  query GetEventTypes {
    eventTypes {
      id
      name
    }
  }
`;

export const GET_MANAGEABLE_SECTIONS = gql`
  query GetManageableSections($input: BackofficeManageableSectionsInput!) {
    manageableSections(input: $input) {
      nodes {
        id
        name
        legacyId
      }
    }
  }
`;

export const GET_UNIVERSITIES = gql`
  query GetUniversities {
    universities {
      id
      name
      address {
        address1
        address2
        city
        state
        zip
      }
    }
  }
`;

export const GET_FACILITIES = gql`
  query GetFacilities($input: BackofficeCRMFacilitiesInput!) {
    facilities(input: $input, first: 10) {
      nodes {
        id
        name
        addresses {
          address1
          address2
          city
          state
          postalCode
        }
      }
    }
  }
`;

export const GET_PRODUCTS = gql`
  query GetProducts($input: BackofficeCommerceProductInput!) {
    commerceProducts(input: $input) {
      nodes {
        id
        name
        description
      }
    }
  }
`;

export const GET_PRODUCT_VARIANTS = gql`
  query GetProductVariants($productId: String, $templateId: String) {
    commerceProductVariants(input: { productId: $productId, templateId: $templateId }) {
      totalCount
      nodes {
        id
        name
        priceCents
        lookupKey
        externalId
      }
    }
  }
`;

export const GET_ADDRESS_TIMEZONE = gql`
  query GetAddressTimezone($address1: String!, $city: String!, $state: String!, $zip: String!) {
    addressTimezone(address1: $address1, city: $city, state: $state, zip: $zip)
  }
`;

export const CREATE_EVENT_MUTATION = gql`
  mutation CreateEvent($input: BackofficeEventsCreateEventInput!) {
    createEvent(input: $input) {
      success
      message
      event {
        slug
      }
    }
  }
`;

export const UPDATE_EVENT_MUTATION = gql`
  mutation UpdateEvent($input: BackofficeEventsUpdateEventInput!) {
    updateEvent(input: $input) {
      success
      message
    }
  }
`;

export const GET_PEOPLE = gql`
  query GetPeople($input: BackofficeCRMPeopleInput!) {
    people(input: $input) {
      nodes {
        id
        pgaId
        displayName
        profilePhoto
      }
    }
  }
`;

export const RECRUITMENT_EVENT_OPTIONS_QUERY = gql`
  query RecruitmentEventOptions {
    purposeOptions: __type(name: "BackofficeRecruitmentPurposeEnum") {
      enumValues {
        name
        description
      }
    }
    strategicFundingOptions: __type(name: "BackofficeRecruitmentStrategicFundingEnum") {
      enumValues {
        name
        description
      }
    }
    pgaRepresentationOptions: __type(name: "BackofficeRecruitmentPGARepresentationEnum") {
      enumValues {
        name
        description
      }
    }
    supportRequestedOptions: __type(name: "BackofficeRecruitmentSupportRequestedEnum") {
      enumValues {
        name
        description
      }
    }
    recruitmentEventTypeOptions: __type(name: "BackofficeRecruitmentRecruitmentEventTypeEnum") {
      enumValues {
        name
        description
      }
    }
  }
`;

export const GET_EVENT_DEFAULTS: TypedDocumentNode<EventDefaultsQueryData, EventDefaultsQueryVariables> = gql`
  query GetEventDefaults($activityTypeId: ID!, $sessionStartDate: ISO8601Date) {
    eventDefaults(activityTypeId: $activityTypeId, sessionStartDate: $sessionStartDate) {
      title
      description
      formVisibility
      location
      orderTemplateId
      enableWaitlist
      locationFacility {
        id
        name
        addresses {
          address1
          address2
          city
          state
          postalCode
        }
      }
      locationVirtualUrl
      section {
        id
        name
        legacyId
      }
      collectsAttendance
      registrationOpenAt
      registrationClosesDaysBefore
      sessions {
        startsAt
        endsAt
        maxAttendance
      }
      feeDefaults {
        paymentCollection
        productVariant {
          id
          name
          lookupKey
          priceCents
          productId
        }
      }
    }
  }
`;

export const FINALIZE_RECRUITMENT_EVENT_MUTATION = gql`
  mutation FinalizeRecruitmentEvent($id: ID!) {
    finalizeRecruitmentEvent(id: $id) {
      success
      message
    }
  }
`;
